import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Organization } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { PlayIcon, PauseIcon, EyeIcon, CheckBadgeIcon, ExclamationTriangleIcon, LinkSlashIcon } from '@heroicons/react/24/outline';

const statusStyles: Record<string, string> = {
  active: 'badge-success',
  suspended: 'badge-danger',
  pending: 'badge-warning',
};

const verificationStyles: Record<string, string> = {
  verified: 'badge-success',
  pending: 'badge-warning',
  flagged: 'badge-danger',
  suspended: 'badge-danger',
};

const BUSINESS_CATEGORY_LABELS: Record<string, string> = {
  fashion: 'Fashion & Apparel',
  health_wellness: 'Health & Wellness',
  technology: 'Technology & Software',
  finance: 'Finance & Business',
  home_garden: 'Home & Garden',
  food_beverage: 'Food & Beverage',
  travel: 'Travel & Lifestyle',
  education: 'Education & Courses',
  beauty: 'Beauty & Personal Care',
  sports: 'Sports & Outdoors',
  entertainment: 'Entertainment & Media',
  services: 'Professional Services',
  ecommerce: 'E-commerce & Retail',
  saas: 'SaaS & Subscriptions',
  marketing: 'Marketing & Advertising',
  other: 'Other',
};

export default function AdminOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  const fetchOrganizations = async () => {
    setIsLoading(true);
    try {
      const params: { status?: string; search?: string } = {};
      if (filter) params.status = filter;
      if (search) params.search = search;

      const response = await adminApi.listOrganizations(params);
      if (response.data.success) {
        setOrganizations(response.data.data.organizations);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [filter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrganizations();
  };

  const handleSuspend = async (orgId: string) => {
    const reason = prompt('Reason for suspension (optional):');
    try {
      await adminApi.suspendOrganization(orgId, reason || undefined);
      toast.success('Organization suspended');
      fetchOrganizations();
    } catch {
      toast.error('Failed to suspend organization');
    }
  };

  const handleActivate = async (orgId: string) => {
    try {
      await adminApi.activateOrganization(orgId);
      toast.success('Organization activated');
      fetchOrganizations();
    } catch {
      toast.error('Failed to activate organization');
    }
  };

  const handleVerify = async (orgId: string) => {
    try {
      await adminApi.updateOrganization(orgId, {
        verificationStatus: 'verified',
        verifiedAt: new Date().toISOString(),
      });
      toast.success('Organization verified');
      fetchOrganizations();
    } catch {
      toast.error('Failed to verify organization');
    }
  };

  const handleFlag = async (orgId: string) => {
    const notes = prompt('Reason for flagging (will be stored):');
    if (notes === null) return;
    try {
      await adminApi.updateOrganization(orgId, {
        verificationStatus: 'flagged',
        verificationNotes: notes,
      });
      toast.success('Organization flagged for review');
      fetchOrganizations();
    } catch {
      toast.error('Failed to flag organization');
    }
  };

  const handleDisconnectStripe = async (orgId: string, orgName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to disconnect ${orgName}'s Stripe Connect account?\n\n` +
      'This will:\n' +
      '• Delete the Connect account from Stripe\n' +
      '• Remove the connection from this organization\n' +
      '• The organization will need to reconnect to receive payouts\n\n' +
      'This action cannot be undone.'
    );
    if (!confirmed) return;

    const reason = prompt('Reason for disconnecting (will be logged):');
    if (reason === null) return;

    try {
      await adminApi.disconnectOrgStripe(orgId, {
        deleteFromStripe: true,
        forceDelete: false,
        reason: reason || 'Disconnected by admin',
      });
      toast.success('Stripe account disconnected successfully');
      fetchOrganizations();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to disconnect Stripe account';
      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-charcoal">Organizations</h2>
        <p className="text-charcoal/70 mt-1">Manage all organizations on the platform</p>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            placeholder="Search organizations..."
          />
        </form>
        <div className="flex gap-2">
          {['', 'active', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                filter === status
                  ? 'bg-sage text-white'
                  : 'bg-cream text-charcoal hover:bg-tan/30'
              )}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-warm-white shadow-sm border border-cream overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sage"></div>
          </div>
        ) : organizations.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No organizations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Verification</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Stripe</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream">
                {organizations.map((org) => (
                  <tr key={org.organizationId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">{org.name}</p>
                        <p className="text-xs text-charcoal/60">{org.slug}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-charcoal/70">
                        {org.businessCategory
                          ? BUSINESS_CATEGORY_LABELS[org.businessCategory] || org.businessCategory
                          : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[org.status || 'pending'])}>
                        {org.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'badge',
                        verificationStyles[org.verificationStatus || 'pending']
                      )}>
                        {org.verificationStatus || 'pending'}
                      </span>
                      {org.verificationNotes && (
                        <p className="text-xs text-rust mt-1" title={org.verificationNotes}>
                          Note: {org.verificationNotes.slice(0, 30)}...
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'badge',
                        org.stripeAccountStatus === 'active' ? 'badge-success' : 'bg-cream text-charcoal/60'
                      )}>
                        {org.stripeAccountStatus || 'Not connected'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-charcoal/60 text-sm">
                      {format(new Date(org.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {org.verificationStatus !== 'verified' && (
                          <button
                            onClick={() => handleVerify(org.organizationId)}
                            className="p-2 text-sage hover:bg-sage/20 rounded-lg transition-colors"
                            title="Verify Organization"
                          >
                            <CheckBadgeIcon className="w-4 h-4" />
                          </button>
                        )}
                        {org.verificationStatus !== 'flagged' && (
                          <button
                            onClick={() => handleFlag(org.organizationId)}
                            className="p-2 text-tan hover:bg-tan/20 rounded-lg transition-colors"
                            title="Flag for Review"
                          >
                            <ExclamationTriangleIcon className="w-4 h-4" />
                          </button>
                        )}
                        {org.status === 'active' ? (
                          <button
                            onClick={() => handleSuspend(org.organizationId)}
                            className="p-2 text-rust hover:bg-rust/20 rounded-lg transition-colors"
                            title="Suspend"
                          >
                            <PauseIcon className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(org.organizationId)}
                            className="p-2 text-sage hover:bg-sage/20 rounded-lg transition-colors"
                            title="Activate"
                          >
                            <PlayIcon className="w-4 h-4" />
                          </button>
                        )}
                        {org.stripeAccountId && (
                          <button
                            onClick={() => handleDisconnectStripe(org.organizationId, org.name)}
                            className="p-2 text-rust hover:bg-rust/20 rounded-lg transition-colors"
                            title="Disconnect Stripe"
                          >
                            <LinkSlashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
